import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import Toast from 'react-native-root-toast';
import * as DocumentPicker from 'expo-document-picker';

import { useAppContext } from '../../../common/utils/context';
import { CREATE_EMPLOYEE_DOCUMENT_RECORD_MOBILE } from '../../graphql/people';
import {
	PEOPLE_DOCUMENT_PICKER_MIME_TYPES,
	PEOPLE_MAX_UPLOAD_BYTES,
	uploadPeopleDocumentAsset,
	uploadPeopleImageAsset,
} from '../../utils/peopleFileUpload';
import usePeoplePhotoPicker from './usePeoplePhotoPicker';

export const NEW_DOCUMENT_UPLOAD_ID = 'new-document';

// The one open document upload on My documents (a request's or a new document):
// its chosen type, picked files, dates, and the upload itself.
const useMyDocumentUpload = ({ employeeId, refetchAll }) => {
	const { getServerEndpoint } = useAppContext();
	const { pickPhoto } = usePeoplePhotoPicker();

	const [uploadAssignmentId, setUploadAssignmentId] = useState(null);
	const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState('');
	const [documentCategory, setDocumentCategory] = useState('');
	// Document types may require several named files (e.g. licence front/back).
	const [pendingAssetsByKey, setPendingAssetsByKey] = useState({});
	const [issuedAt, setIssuedAt] = useState('');
	const [expiresAt, setExpiresAt] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [createDocumentRecord] = useMutation(CREATE_EMPLOYEE_DOCUMENT_RECORD_MOBILE);

	const startUpload = useCallback((assignment) => {
		setUploadAssignmentId(assignment.id);
		setSelectedDocumentTypeId('');
		setDocumentCategory('');
		setPendingAssetsByKey({});
		setIssuedAt('');
		setExpiresAt('');
	}, []);

	const startNewUpload = useCallback(() => {
		setUploadAssignmentId(NEW_DOCUMENT_UPLOAD_ID);
		setSelectedDocumentTypeId('');
		setDocumentCategory('');
		setPendingAssetsByKey({});
		setIssuedAt('');
		setExpiresAt('');
	}, []);

	const cancelUpload = useCallback(() => {
		setUploadAssignmentId(null);
		setSelectedDocumentTypeId('');
		setDocumentCategory('');
		setPendingAssetsByKey({});
		setIssuedAt('');
		setExpiresAt('');
	}, []);

	const changeDocumentType = useCallback((value) => {
		setSelectedDocumentTypeId(value);
		setPendingAssetsByKey({});
		setIssuedAt('');
		setExpiresAt('');
	}, []);

	const addPhoto = useCallback(async (fileKey) => {
		const asset = await pickPhoto();
		if (asset) {
			setPendingAssetsByKey(previousAssets => ({
				...previousAssets,
				[fileKey]: { ...asset, uploadType: 'photo' },
			}));
		}
	}, [pickPhoto]);

	const addFile = useCallback(async (fileKey) => {
		try {
			const result = await DocumentPicker.getDocumentAsync({
				type: PEOPLE_DOCUMENT_PICKER_MIME_TYPES,
				copyToCacheDirectory: true,
				multiple: false,
			});

			if (result?.canceled || !result?.assets?.[0]) {
				return;
			}

			const [asset] = result.assets;
			if (Number.isFinite(asset.size) && asset.size > PEOPLE_MAX_UPLOAD_BYTES) {
				Toast.show('File must be 10 MB or smaller');
				return;
			}

			setPendingAssetsByKey(previousAssets => ({
				...previousAssets,
				[fileKey]: { ...asset, fileName: asset.name, uploadType: 'document' },
			}));
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to open the file picker');
		}
	}, []);

	const submitUpload = useCallback(async ({ assignment, documentType, category }, requiredFiles) => {
		if (!employeeId) {
			return;
		}

		setIsSubmitting(true);
		try {
			const files = [];

			for (const requiredFile of requiredFiles) {
				const asset = pendingAssetsByKey[requiredFile.key];
				if (!asset) {
					continue;
				}

				const uploadAsset = asset.uploadType === 'document'
					? uploadPeopleDocumentAsset
					: uploadPeopleImageAsset;
				const uploadedFile = await uploadAsset({
					asset,
					employeeId,
					getServerEndpoint,
				});

				if (!uploadedFile) {
					Toast.show('File upload failed');
					return;
				}

				files.push({
					key: requiredFile.key,
					s3Key: uploadedFile.s3Key,
					mimeType: uploadedFile.mimeType,
					sizeBytes: uploadedFile.sizeBytes,
				});
			}

			await createDocumentRecord({
				variables: {
					input: {
						employee: employeeId,
						title: documentType?.name || String(category || '').trim(),
						category: documentType?.name || String(category || '').trim(),
						documentType: documentType?.id || null,
						assignment: assignment?.id || undefined,
						files,
						issuedAt: issuedAt || undefined,
						expiresAt: expiresAt || undefined,
					},
				},
			});

			Toast.show('Document uploaded');
			cancelUpload();
			await refetchAll();
		}
		catch (error) {
			console.error('Document upload failed', error);
			Toast.show(error?.message || 'Unable to upload document');
		}
		finally {
			setIsSubmitting(false);
		}
	}, [cancelUpload, createDocumentRecord, employeeId, expiresAt, getServerEndpoint, issuedAt, pendingAssetsByKey, refetchAll]);

	return {
		uploadAssignmentId,
		selectedDocumentTypeId,
		documentCategory,
		setDocumentCategory,
		pendingAssetsByKey,
		issuedAt,
		setIssuedAt,
		expiresAt,
		setExpiresAt,
		isSubmitting,
		startUpload,
		startNewUpload,
		cancelUpload,
		changeDocumentType,
		addPhoto,
		addFile,
		submitUpload,
	};
};

export default useMyDocumentUpload;
